import React, { FC } from 'react'
import styled from 'styled-components'
import { calculateBagInDollar } from '../../../logic/financials/calculate-bag-in-dollar'
import { add } from '../../../utils/static-web3'
import { DepositPopup } from './DepositPopup'
import { usePools } from '../../../hooks/use-pools'
import { Link, useNavigate } from 'react-router-dom'
import { PoolWithData, TokenWithData } from '../../../api/api-types'
import {
  PoolComposition,
  PoolCompositionLogoSymbol,
  PoolCompositionSymbol,
  PoolCompositionWeight,
} from '../PoolComposition'
import CurrencyLogo from '../../CurrencyLogo'
import { coinImage } from '../../../assets/coins/coins'
import PercentageValue from '../../PercentageValue'
import CurrencyValue from '../../CurrencyValue'
import { HideSmallerThan } from '../../catalog/responsive/Hide'
import { EmptyLine } from '../../catalog/EmptyLine'
import { SwaapLargeLoader } from '../../catalog/SwaapLoader'
import { NetAPR, Past24hVolume } from '../../catalog/MouseoverLegend'
import { createEarnTabEvent, createSuccessEvent } from 'user-events-segment'
import { desktopSize } from '../../catalog/screen-sizes'

export const DepositPageWrapper = styled.div`
  width: 80%;
  align-self: center;
`

export interface Props {
  poolId: string
}

export const DepositPage: FC<Props> = ({ poolId }) => {
  const { pools } = usePools()

  const selectedLine = pools.find(p => p.pool.address === poolId)
  const navigate = useNavigate()
  if (!selectedLine) {
    return (
      <DepositPageWrapper>
        <EmptyLine block />
        <EmptyLine block />
        <SwaapLargeLoader />
      </DepositPageWrapper>
    )
  }
  return (
    <DepositPageWrapper>
      {/*
      <h4 className={'ml20'}>
        Pool {getPoolName(poolId)}{' '}
        <a
          className="link fs32 p5"
          href={POLYGON_CONFIG.getExplorerPoolUrl(poolId)}
          target="_blank"
        >
          ↗
        </a>
      </h4>
    */}
      <EmptyLine block />

      <PageGrid>
        <SideContent>
          {/*<Link className="back-link" to={'/'}>
          <div className={'mb20 ml20 fs12'} style={{display:'flex',alignItems:'center',justifyContent:'start'}}>
            <ArrowBackFixed /> <span className={'ml10'}>Back to pools</span>
          </div>
        </Link>*/}
          <EmptyLine />
          <EmptyLine />
          <EmptyLine />
          <HideSmallerThan limit={desktopSize}>
            <Line>
              <Term>&nbsp;</Term>
              <Def>{getPoolComposition(selectedLine.pool)}</Def>
            </Line>

            <Line>
              <Term>TVL</Term>
              <Def>
                {/* Note that this component has its own font-size which is not a good idea,
                or should be a props */}
                <CurrencyValue
                  precision={3}
                  currency="$"
                  value={selectedLine.tvl ? selectedLine.tvl : 0}
                  numericFontSize="3rem"
                  nonNumericFontSize={'1.75rem'}
                />
              </Def>
            </Line>

            <Line>
              <Term
                onMouseOver={() => {
                  createEarnTabEvent(selectedLine.pool).hover_volume_tooltip(
                    'start'
                  )
                }}
                onMouseOut={() => {
                  createEarnTabEvent(selectedLine.pool).hover_volume_tooltip(
                    'end'
                  )
                }}
              >
                <Past24hVolume>
                  <span>
                    Volume{' '}
                    <span
                      style={{
                        fontSize: '1.1rem',
                        padding: '0px 5px 0px 1px',
                      }}
                    >
                      (past 24h)
                    </span>
                  </span>
                </Past24hVolume>
              </Term>
              <Def>
                {/* Note that this component has its own font-size which is not a good idea, 
                or should be a props */}
                <CurrencyValue
                  precision={3}
                  currency="$"
                  value={
                    selectedLine.dailyVolume ? selectedLine.dailyVolume : 0
                  }
                  numericFontSize="3rem"
                  nonNumericFontSize={'1.75rem'}
                />
              </Def>
            </Line>

            <Line>
              <Term
                onMouseOver={() => {
                  createEarnTabEvent(selectedLine.pool).hover_net_apr_tooltip(
                    'start'
                  )
                }}
                onMouseOut={() => {
                  createEarnTabEvent(selectedLine.pool).hover_net_apr_tooltip(
                    'end'
                  )
                }}
              >
                <NetAPR>
                  <span>
                    Net APR{' '}
                    <span
                      style={{ fontSize: '1.1rem', padding: '0px 5px 0px 1px' }}
                    >
                      (since inception)
                    </span>
                  </span>
                </NetAPR>
              </Term>
              <Def>
                {/* Note that this component has its own font-size which is not a good idea, 
                or should be a props */}
                <PercentageValue
                  value={selectedLine.net_apr_since_inception}
                  numericFontSize="3rem"
                  nonNumericFontSize={'1.75rem'}
                />
              </Def>
            </Line>
          </HideSmallerThan>
        </SideContent>

        <DepositPopup
          line={selectedLine}
          onExit={() => {
            navigate('/')
          }}
          onDeposit={async depositResult => {
            const value = await calculateBagInDollar(depositResult.amounts)
            const currentValue = selectedLine!.userValue || 0
            selectedLine!.userValue = currentValue + value
            const currentTvl = selectedLine!.tvl || 0
            selectedLine!.tvl = currentTvl + value
            selectedLine!.userShares = add(
              selectedLine!.userShares!,
              depositResult.value
            )

            //navigate('/')
          }}
        />
      </PageGrid>
    </DepositPageWrapper>
  )
}

const PageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 10px;
  justify-content: space-between;
  align-self: center;
`

const Line = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 20px;
`
const Term = styled.span`
  font-size: 1.5rem;
  flex: 1;
  text-align: right;
  margin-right: 20px;
  font-family: Montserrat-Light;
`
const Def = styled.span`
  align-self: flex-end;
  font-size: 1.5rem;
`
const SideContent = styled.div`
  //background-color: yellow;
  margin-right: 200px;
  justify-content: center;
  align-items: center;
`

function getPoolComposition(pool: PoolWithData) {
  return (
    <span className={'flex-row'}>
      {pool.tokens.map((token: TokenWithData, index: number) => {
        return (
          <div style={{ margin: '0.5rem' }} key={index}>
            <PoolComposition key={`pool-composition-${index}`}>
              <PoolCompositionLogoSymbol key={`logo-symbol-${index}`}>
                <CurrencyLogo
                  key={`logo-${index}`}
                  src={coinImage(token['symbol'])!}
                />
                <PoolCompositionSymbol key={`pool-composition-symbol-${index}`}>
                  {token['symbol']}
                </PoolCompositionSymbol>
              </PoolCompositionLogoSymbol>
              <PoolCompositionWeight key={`pool-composition-weight-${index}`}>
                {Math.round(token.percent)}%
              </PoolCompositionWeight>
            </PoolComposition>
          </div>
        )
      })}
    </span>
  )
}
